import { useCallback, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useJobPolling } from 'hooks/useJobPolling';

import spinner from 'assets/remix-spinner.gif';

import FilesService from 'services/files';
import JobsService from 'services/jobs';

import { errorReactToastify } from 'utlis/toasts';

import styles from './styles.module.scss';

const ACCEPTED_VIDEO_TYPES = 'video/mp4,video/quicktime,video/webm';
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.mov,.webm';

const UPLOAD_PHASES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  CREATING_JOB: 'creating_job',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

const VideoUpload = ({ onFramesExtracted, onCancel, maxSizeMB = 100 }) => {
  const [phase, setPhase] = useState(UPLOAD_PHASES.IDLE);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleJobCompleted = useCallback(outputPayload => {
    setPhase(UPLOAD_PHASES.COMPLETED);
    onFramesExtracted?.(outputPayload?.frames || []);
  }, [onFramesExtracted]);

  const handleJobFailed = useCallback(error => {
    setPhase(UPLOAD_PHASES.FAILED);
    errorReactToastify(
      error || 'Video processing failed. Please try again.',
      {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      }
    );
  }, []);

  const { jobPollStatus, stopPolling } = useJobPolling({
    jobId,
    interval: 3000,
    onCompleted: handleJobCompleted,
    onFailed: handleJobFailed,
  });

  const validateFile = file => {
    const validTypes = ACCEPTED_VIDEO_TYPES.split(',');

    if (!validTypes.includes(file.type)) {
      errorReactToastify('Please select a valid video file (MP4, MOV, or WEBM).');

      return false;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      errorReactToastify(`Video must be smaller than ${maxSizeMB}MB.`);

      return false;
    }

    return true;
  };

  const handleUpload = async file => {
    if (!validateFile(file)) {
      return;
    }

    // eslint-disable-next-line compat/compat
    const controller = new AbortController();

    abortControllerRef.current = controller;
    setSelectedFile(file);
    setPhase(UPLOAD_PHASES.UPLOADING);
    setUploadProgress(0);

    try {
      // Step 1: Upload to S3
      const videoUrl = await FilesService.uploadFileFromLocal(file, {
        signal: controller.signal,
        onUploadProgress: ({ progress }) => {
          setUploadProgress(Math.round(progress * 100));
        },
      });

      // Step 2: Create job
      setPhase(UPLOAD_PHASES.CREATING_JOB);
      const jobResponse = await JobsService.createJob(
        'video_frame_extraction',
        { video_url: videoUrl },
        controller.signal
      );

      // Step 3: Start polling (backend returns uuid, not job_id)
      const newJobId = jobResponse.uuid || jobResponse.job_id;

      setPhase(UPLOAD_PHASES.PROCESSING);
      setJobId(newJobId);
    }
    catch (err) {
      if (!axios.isCancel(err)) {
        setPhase(UPLOAD_PHASES.FAILED);
        errorReactToastify('Failed to upload video. Please try again.');
      }
    }
  };

  const handleFileSelect = event => {
    const file = event.target.files?.[0];

    if (file) {
      handleUpload(file);
    }

    event.target.value = '';
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    stopPolling();
    setPhase(UPLOAD_PHASES.IDLE);
    setJobId(null);
    setSelectedFile(null);
    setUploadProgress(0);
    onCancel?.();
  };

  const handleRetry = () => {
    setPhase(UPLOAD_PHASES.IDLE);
    setJobId(null);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  // Phase: IDLE
  if (phase === UPLOAD_PHASES.IDLE) {
    return (
      <div className={styles['video-upload-container']}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_VIDEO_EXTENSIONS}
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-gray-800 text-sm font-normal cursor-pointer transition-colors
            w-full bg-white border border-gray-300 rounded-lg shadow-sm mx-auto h-[44px] max-w-[310px] px-4 py-3"
        >
          Select a video file
        </button>
        <div className="text-gray-400 text-xs">
          MP4, MOV, WEBM (max
          {' '}
          {maxSizeMB}
          MB)
        </div>
      </div>
    );
  }

  // Phase: CREATING_JOB
  if (phase === UPLOAD_PHASES.CREATING_JOB) {
    return (
      <div className={styles['processing-state']}>
        <ImpulseSpinner frontColor="#28BDAE" />
        <p className={styles['status-text']}>
          Starting processing...
        </p>
      </div>
    );
  }

  // Phase: PROCESSING or uploading
  if (phase === UPLOAD_PHASES.PROCESSING || phase === UPLOAD_PHASES.UPLOADING) {
    return (
      <div className={styles['processing-state']}>
        <img src={spinner} alt="Processing" className="w-[34px] h-[34px]" />
        <p className={styles['status-text']}>
          Loading your designs...
        </p>
      </div>
    );
  }

  // Phase: FAILED
  if (phase === UPLOAD_PHASES.FAILED) {
    return (
      <div className={styles['error-state']}>
        <p className={styles['error-text']}>
          Processing failed
        </p>
        <button type="button" className={styles['retry-button']} onClick={handleRetry}>
          Try Again
        </button>
      </div>
    );
  }

  return null;
};

VideoUpload.propTypes = {
  onFramesExtracted: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  maxSizeMB: PropTypes.number,
};

export default VideoUpload;
